import { useRouter } from 'expo-router';
import { ScrollView } from 'react-native';
import { YStack } from 'tamagui';

import { LegalParagraph, LegalSection } from '@/components/legal-section';
import { ScreenContainer } from '@/components/screen-container';
import { useDockClearance, useDockScroll } from '@/lib/dock';
import { ScreenHeader } from '@/components/screen-header';
import { space } from '@/theme/tokens.generated';

// Issue #134 (part of the #159 Legal trust surfaces epic): sign-up's
// own "By continuing, you agree to Nearkin's Terms of Service and
// Privacy Policy" line linked to nothing -- no document existed
// anywhere in the app or repo (confirmed by grep before writing this).
// This is a real, substantive draft grounded in this project's own
// design.md commitments (the real-photos clause, the emergency-service
// disclaimer, the report/moderation timeline), strengthened with a
// standard set of liability-limiting clauses (indemnification,
// arbitration + class-action waiver, an explicit assumption-of-risk
// release for in-person meetups, a liability cap, governing-law/
// severability boilerplate) -- founder-requested same-day follow-up,
// since none of this existed in the first draft.
//
// This is still a founder-written draft, not law-firm-reviewed legal
// advice -- and no amount of ToS drafting substitutes for the two
// things that actually determine real liability exposure here: (1) a
// registered business entity (an LLC, at minimum) -- without one, a
// liability-limitation clause protects nothing, because there is no
// legal person named "Nearkin" distinct from its founder; (2) general
// liability insurance -- a liability cap is a legal defense, not a
// source of funds to pay a claim. Section 11's arbitration/class-
// waiver clause also cannot apply to sexual assault/harassment claims
// -- the federal Ending Forced Arbitration Act (2022) voids forced
// arbitration for exactly that claim category, which is the single
// most relevant legal fact for an app whose core function is
// facilitating in-person meetups between strangers. Have this
// reviewed by an actual attorney, and form a real business entity,
// before any real public launch.
//
// A bundled in-app screen, not a hosted external page -- the AC's own
// language ("hosted pages or bundled screens") allows either, and a
// bundled screen needs no separate hosting/deploy setup, stays
// versioned with the app itself, and matches every other document-like
// screen this app already has (none exist yet, but this establishes
// the pattern privacy/index.tsx immediately follows). Reachable from
// sign-up now; also reachable from Settings once #87 ships (that
// screen doesn't exist yet -- this route itself has no auth guard, see
// root _layout.tsx's Stack.Screen registration, so it already works
// from both a signed-out and signed-in context without changes once
// #87 adds its own link here).
export function Terms() {
  const router = useRouter();
  const dockClearance = useDockClearance();
  const dockScroll = useDockScroll();

  return (
    <ScreenContainer scrollUnderDock>
      <YStack pt="$6" pb="$2">
        <ScreenHeader
          title="Terms of Service"
          subtitle="Last updated September 15, 2026"
          onBack={() => router.back()}
        />
      </YStack>
      <ScrollView
        style={{ flex: 1 }}
        {...dockScroll}
        contentContainerStyle={{ paddingTop: space[2], paddingBottom: space[8] + dockClearance, gap: space[5] }}
      >
        <LegalSection title="1. Acceptance and eligibility">
          <LegalParagraph>
            By creating a Nearkin account, you agree to these Terms of Service and to our Privacy Policy. Nearkin is for
            people 18 and older only. By signing up you confirm you meet this requirement.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="2. What Nearkin is">
          <LegalParagraph>
            Nearkin lets you post and browse threads with people near you. Your handle, bio, prompts, and interests are
            visible to other nearby users by default. Your photos are hidden until you and another user mutually agree
            to reveal them to each other. Your precise location is never shown to other users — only your general
            distance, and only after a match&apos;s radius covers it.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="3. Your content and your photos">
          <LegalParagraph>
            You&apos;re responsible for everything you post. Content that is illegal, harassing, hateful, sexually
            exploitative, or that impersonates someone else is not allowed.
          </LegalParagraph>
          <LegalParagraph>
            Every photo you upload must be a real, current photo of yourself — not an AI-generated image, not a stock
            photo, and not a photo of someone else. We enforce this through user reports and manual review, and we may
            remove photos or suspend accounts that violate it.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="4. Reporting and moderation">
          <LegalParagraph>
            You can report any user, thread, or reply. Routine reports are reviewed within 24 hours. Reports you mark as
            severe (immediate safety concern) trigger an urgent alert to our team so we can respond as quickly as
            possible, even outside normal hours.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="5. Not an emergency service">
          <LegalParagraph>
            Nearkin is not an emergency service and reports are not monitored in real time around the clock. If you or
            someone else is in immediate danger, contact your local emergency services directly — do not rely on an
            in-app report to reach anyone quickly enough for a real emergency.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="6. Suspension and termination">
          <LegalParagraph>
            We may suspend or terminate your account for violating these terms, for behavior that endangers other users,
            or for repeated or severe reports against you. We may also remove content that violates these terms at any
            time.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="7. Meeting people in person — assumption of risk">
          <LegalParagraph>
            Nearkin connects you with real people nearby, but we don&apos;t run background checks and can&apos;t
            guarantee anyone&apos;s identity, intentions, or safety. If you choose to meet someone, meet in a public
            place the first time and tell a friend or family member where you&apos;re going.
          </LegalParagraph>
          <LegalParagraph>
            You understand and agree that any in-person meeting arranged through Nearkin is entirely at your own risk.
            To the fullest extent permitted by law, you release Nearkin, its founder, and anyone working on its behalf
            from any and all claims, damages, or losses arising from your interactions with other users, whether online
            or in person.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="8. Indemnification">
          <LegalParagraph>
            You agree to defend, indemnify, and hold harmless Nearkin, its founder, and anyone working on its behalf
            from any claim, damage, loss, or expense (including reasonable attorneys&apos; fees) arising out of your use
            of the app, your content, your violation of these terms, or your interactions with other users.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="9. Disclaimer of warranties">
          <LegalParagraph>
            Nearkin is provided &quot;as is&quot; and &quot;as available,&quot; without warranties of any kind, express
            or implied, including implied warranties of merchantability, fitness for a particular purpose, and
            non-infringement. We don&apos;t warrant that the app will be uninterrupted, error-free, or that any user you
            interact with is who they claim to be.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="10. Limitation of liability">
          <LegalParagraph>
            To the fullest extent permitted by law, Nearkin and its founder are not liable for any indirect, incidental,
            special, consequential, or punitive damages, or any loss of data, arising from your use of the app. Our
            total liability for any claim arising from these terms or your use of Nearkin will not exceed $100 or the
            amount you paid us in the past 12 months, whichever is greater.
          </LegalParagraph>
          <LegalParagraph>
            Some jurisdictions don&apos;t allow the exclusion of certain warranties or the limitation of certain
            damages, so some of the limitations above may not apply to you.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="11. Dispute resolution — arbitration and class action waiver">
          <LegalParagraph>
            You and Nearkin agree to resolve any dispute arising from these terms or your use of the app through binding
            individual arbitration, not in court, except that either of us may bring an individual claim in small claims
            court. You and Nearkin each waive the right to a jury trial and to participate in a class action or class
            arbitration.
          </LegalParagraph>
          <LegalParagraph>
            This arbitration agreement does not apply to a claim of sexual assault or sexual harassment, or to any other
            claim that applicable law does not allow to be arbitrated — for those claims, either of us may go to court.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="12. Governing law and venue">
          <LegalParagraph>
            {/* Deliberately unfilled -- see this file's own doc comment: no
                business entity or state of formation exists yet anywhere in
                this project's docs. Naming a state here before one is
                actually chosen would misstate a real legal fact, which is
                worse than an honest placeholder. Fill this in once a
                business entity exists. */}
            These terms are governed by the laws of [STATE — to be filled in once Nearkin has a registered business
            entity], without regard to conflict-of-law principles.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="13. General">
          <LegalParagraph>
            If any part of these terms is found unenforceable, the rest remains in effect. These terms, together with
            our Privacy Policy, are the entire agreement between you and Nearkin regarding your use of the app.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="14. Changes to these terms">
          <LegalParagraph>
            We may update these terms as the product changes. We&apos;ll update the date at the top of this page when we
            do. Continuing to use Nearkin after a change means you accept the updated terms.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="15. Contact">
          <LegalParagraph>Questions about these terms? Reach us at hello@nearkin.co.</LegalParagraph>
        </LegalSection>
      </ScrollView>
    </ScreenContainer>
  );
}
